/**
 * A cara de cada categoria de aviso (parte D1, 01/10/2026): ícone, rótulo curto (o rodapé
 * do aviso na tela) e descrição (a tela de preferências) — desenho §1. A lista de
 * categorias, os rótulos longos e a tradução evento → categoria são do servidor
 * (`app/services/preferencias_de_aviso.py`); o evento ao vivo traz a `categoria`.
 */
import {
  AlarmClock,
  AtSign,
  Bell,
  CalendarDays,
  DoorOpen,
  FileImage,
  Headset,
  KanbanSquare,
  Megaphone,
  MessageCircle,
  MessageSquare,
  MessageSquareText,
  Newspaper,
  PhoneMissed,
  ShieldAlert,
  Users,
  type LucideIcon,
} from "lucide-react";

export interface ApresentacaoDaCategoria {
  rotuloCurto: string;
  descricao: string;
  Icone: LucideIcon;
  /**
   * O rótulo do 3º nível (`sino`) quando não é "Só no sino" (desenho do portal 0.4): o chat e o Atendimento do portal chamam
   * esse nível de "contador". Antes a `LinhaDeAviso` decidia pelo prefixo `chat_` da chave — a 3ª exceção seria `portal_`.
   */
  rotuloDoSino?: string;
}

export const CATEGORIAS_DE_AVISO: Record<string, ApresentacaoDaCategoria> = {
  feed_posts: { rotuloCurto: "Feed", descricao: "Cada publicação nova no mural", Icone: Newspaper },
  feed_comigo: {
    rotuloCurto: "Feed",
    descricao: "Comentário, menção, elogio ou homenagem a você",
    Icone: MessageSquareText,
  },
  chat_mencoes: {
    rotuloCurto: "Menção",
    descricao: "Quando citam você numa conversa",
    Icone: AtSign,
  },
  chat_diretas: {
    rotuloCurto: "Conversa direta",
    descricao: "Mensagens de uma pessoa para você",
    Icone: MessageCircle,
    rotuloDoSino: "Só no contador do chat",
  },
  chat_grupos: {
    rotuloCurto: "Grupo",
    descricao: "Mensagens, quem entrou ou saiu, grupo arquivado",
    Icone: Users,
    rotuloDoSino: "Só no contador do chat",
  },
  ligacoes_perdidas: {
    rotuloCurto: "Ligações perdidas",
    descricao: "Quando alguém liga e você não atende.",
    Icone: PhoneMissed,
  },
  agenda: {
    rotuloCurto: "Agenda",
    descricao: "Convites, lembretes e mudanças de horário",
    Icone: CalendarDays,
  },
  lembretes: {
    rotuloCurto: "Lembrete",
    descricao: "Lembrete marcado por você nas suas notas",
    Icone: AlarmClock,
  },
  tarefas: {
    rotuloCurto: "Tarefas",
    descricao: "Tarefa atribuída, prazo e mudanças",
    Icone: KanbanSquare,
  },
  salas: { rotuloCurto: "Salas", descricao: "Reservas e mudanças de sala", Icone: DoorOpen },
  atendimentos: {
    rotuloCurto: "Atendimentos",
    descricao: "Atendimento atribuído, transferido ou aguardando",
    Icone: Headset,
  },
  alertas_sistema: {
    rotuloCurto: "Sistema",
    descricao: "Manutenção, falhas e avisos da TI",
    Icone: ShieldAlert,
  },
  outros: {
    rotuloCurto: "Aviso",
    descricao: "O que não se encaixa nas categorias acima",
    Icone: Bell,
  },
};

/**
 * As 3 categorias do PORTAL (D3, passo 19): a cara delas. A lista, os rótulos longos e a regra do nível são do servidor
 * (`CATEGORIAS_DO_PORTAL` em `preferencias_de_aviso.py`); o evento ao vivo traz a `categoria`. Ficam FORA de
 * `CATEGORIAS_DE_AVISO` de propósito (o teste do espelho cravou as 13 internas e o parceiro nunca recebe as internas).
 */
export const CATEGORIAS_DO_PORTAL: Record<string, ApresentacaoDaCategoria> = {
  portal_central: {
    rotuloCurto: "Central",
    descricao: "Comunicados publicados para você",
    Icone: Megaphone,
  },
  portal_comprovantes: {
    rotuloCurto: "Comprovante",
    descricao: "Comprovante devolvido para reenvio",
    Icone: FileImage,
  },
  portal_atendimento: {
    rotuloCurto: "Atendimento",
    descricao: "Resposta da equipe nas suas conversas",
    Icone: MessageSquare,
    rotuloDoSino: "Só no contador do Atendimento",
  },
};

export function apresentacaoDaCategoria(
  chave?: string | null,
  mapa: Record<string, ApresentacaoDaCategoria> = CATEGORIAS_DE_AVISO
): ApresentacaoDaCategoria {
  return (chave && mapa[chave]) || CATEGORIAS_DE_AVISO.outros;
}
